import { ProspectApi } from '../api/prospect-api';
import { ModalComponent } from '../components/modal';
import { ToastComponent } from '../components/toast';
import { 
  UserProfile, 
  RelationType,
  ApporteurTab
} from '../types';
import { 
  formatSiretDisplay, 
  formatDate, 
  formatDateTime, 
  formatRelationType, 
  escapeHtml 
} from '../utils/formatters';

export class ApporteurView {
  private currentUser: UserProfile | null = null;

  constructor() {
    this.initEventListeners();
  }

  public setUser(user: UserProfile): void {
    this.currentUser = user;
    this.renderHeader();
    this.loadProspects();
    this.loadNotifications();
    this.switchTab('add');
  }

  private initEventListeners(): void {
    // Portal navigation buttons
    document.getElementById('tabAddProspectBtn')?.addEventListener('click', () => this.switchTab('add'));
    document.getElementById('tabMyProspectsBtn')?.addEventListener('click', () => this.switchTab('list'));
    document.getElementById('tabMyMailsBtn')?.addEventListener('click', () => this.switchTab('mails'));

    // Refresh buttons
    document.getElementById('refreshMyProspectsBtn')?.addEventListener('click', () => this.loadProspects());
    document.getElementById('refreshNotifBtn')?.addEventListener('click', () => this.loadNotifications());

    // Prospect form submit
    const form = document.getElementById('prospectForm') as HTMLFormElement | null;
    form?.addEventListener('submit', (e) => this.handleProspectSubmit(e));
  }

  public renderHeader(): void {
    if (!this.currentUser) return;

    const greeting = document.getElementById('apporteurGreeting');
    if (greeting) greeting.textContent = this.currentUser.raison_sociale;

    const metaTags = document.getElementById('apporteurMetaTags');
    if (metaTags) {
      metaTags.innerHTML = `
        <span class="user-tag-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          SIRET : <strong>${formatSiretDisplay(this.currentUser.siret)}</strong>
        </span>
        <span class="user-tag-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          ${escapeHtml(this.currentUser.email)}
        </span>
        <span class="user-tag-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          ${escapeHtml(this.currentUser.telephone)}
        </span>
      `;
    }
  }

  public switchTab(tab: ApporteurTab): void {
    const tabButtons: Record<ApporteurTab, string> = {
      add: 'tabAddProspectBtn',
      list: 'tabMyProspectsBtn',
      mails: 'tabMyMailsBtn'
    };
    const tabPanels: Record<ApporteurTab, string> = {
      add: 'addProspectTab',
      list: 'myProspectsTab',
      mails: 'myMailsTab'
    };
    const tabs = Object.keys(tabButtons) as ApporteurTab[];

    tabs.forEach(t => {
      const btnId = tabButtons[t];
      const panelId = tabPanels[t];

      const btn = document.getElementById(btnId);
      const panel = document.getElementById(panelId);

      if (btn && panel) {
        if (t === tab) {
          btn.classList.add('active');
          panel.style.display = 'block';
        } else {
          btn.classList.remove('active');
          panel.style.display = 'none';
        }
      }
    });

    if (tab === 'list') this.loadProspects();
    if (tab === 'mails') this.loadNotifications();
  }

  private async handleProspectSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const errorAlert = document.getElementById('prospectErrorAlert');
    if (errorAlert) errorAlert.style.display = 'none';

    try {
      const siret = (form.elements.namedItem('siret') as HTMLInputElement).value;
      const registration_date = (form.elements.namedItem('registration_date') as HTMLInputElement).value;
      const contact_position = (form.elements.namedItem('contact_position') as HTMLInputElement).value;
      const company_size = (form.elements.namedItem('company_size') as HTMLSelectElement).value;
      const relation_type = (form.querySelector('input[name="relation_type"]:checked') as HTMLInputElement).value as RelationType;
      const relation_details = (form.elements.namedItem('relation_details') as HTMLTextAreaElement).value;

      const res = await ProspectApi.createProspect({
        siret,
        registration_date,
        contact_position,
        company_size,
        relation_type,
        relation_details
      });

      ToastComponent.show(res.message, 'success');

      // Trigger recap modal
      ModalComponent.openRecapModal(res.prospect, res.is_duplicate, res.duplicate_message);

      this.loadProspects();
      this.loadNotifications();
    } catch (err: any) {
      if (errorAlert) {
        errorAlert.textContent = err.message;
        errorAlert.style.display = 'block';
      }
      ToastComponent.show(err.message, 'error');
    }
  }

  public async loadProspects(): Promise<void> {
    try {
      const list = await ProspectApi.getMyProspects();
      const countEl = document.getElementById('myProspectsCount');
      if (countEl) countEl.textContent = String(list.length);

      const tbody = document.getElementById('myProspectsTableBody');
      if (!tbody) return;

      if (list.length === 0) {
        tbody.innerHTML = `
          <tr>
            <td colspan="7" style="text-align:center; padding: 2.5rem; color: var(--text-dim);"
            >Vous n'avez pas encore déclaré d'opportunité prospect.</td>
          </tr>
        `;
        return;
      }

      tbody.innerHTML = list.map(p => `
        <tr>
          <td><span class="siret-code">${formatSiretDisplay(p.siret)}</span></td>
          <td>${formatDate(p.registration_date)}</td>
          <td><strong>${escapeHtml(p.contact_position)}</strong></td>
          <td style="font-size:0.82rem;">${escapeHtml(p.company_size)}</td>
          <td><span class="relation-badge relation-${p.relation_type}">${formatRelationType(p.relation_type)}</span></td>
          <td><div class="relation-details-box" style="max-width:280px;">${escapeHtml(p.relation_details || 'Aucune')}</div></td>
          <td>
            ${p.is_shared 
              ? `<span class="badge-tag badge-shared" title="Ce SIRET est partagé par un autre partenaire">Déjà référencé</span>` 
              : `<span class="badge-tag badge-unique">Référent Unique</span>`}
          </td>
        </tr>
      `).join('');
    } catch (err: any) {
      console.error('Error loading my prospects:', err);
    }
  }

  public async loadNotifications(): Promise<void> {
    try {
      const list = await ProspectApi.getMyNotifications();
      const notifBadge = document.getElementById('notifCountBadge');
      const mailsCount = document.getElementById('myMailsCount');
      if (notifBadge) notifBadge.textContent = String(list.length);
      if (mailsCount) mailsCount.textContent = String(list.length);

      const container = document.getElementById('myNotificationsList');
      if (!container) return;

      if (list.length === 0) {
        container.innerHTML = `
          <div style="text-align: center; padding: 2.5rem; color: var(--text-dim);">
            <div style="font-size: 2rem; margin-bottom: 0.5rem;">📭</div>
            Aucune notification reçue pour le moment.
          </div>
        `;
        return;
      }

      container.innerHTML = list.map(n => `
        <div class="notification-card" data-notification-id="${n.id}">
          <div>
            <div class="notification-subject">✉️ ${escapeHtml(n.subject)}</div>
            <div class="notification-time">Reçu le ${formatDateTime(n.created_at)} — Prospect SIRET : ${formatSiretDisplay(n.prospect_siret)}</div>
          </div>
          <button class="btn btn-outline btn-sm">Lire le message</button>
        </div>
      `).join('');

      // Attach click listeners to cards
      container.querySelectorAll('.notification-card').forEach((card, idx) => {
        card.addEventListener('click', () => {
          ModalComponent.openEmailModal(list[idx]);
        });
      });
    } catch (err: any) {
      console.error('Error loading notifications:', err);
    }
  }
}
